import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { useAuth, useTheme } from "../App";
import Lightbox from "../components/Lightbox";
import PrenomsSection from "../components/PrenomsSection";
import AudioButton from "../components/AudioButton";
import { 
  Volume2, 
  ArrowRight,
  Moon,
  Sun,
  Menu,
  X,
  Search,
  Play,
  Heart,
  MessageCircle,
  Share2,
  ChevronRight,
  Headphones,
  BookOpen,
  Users,
  Flame,
  Clock,
  Eye,
  TrendingUp,
  ArrowUp,
  Calendar,
  Maximize2
} from "lucide-react";
import { Progress } from "../components/ui/progress";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to build image URL
const getImageUrl = (url) => {
  if (!url) return "/api/images/hero_culture_baoule.png";
  if (url.startsWith("http")) return url; // External URL (legacy)
  if (url.startsWith("/api/")) return `${BACKEND_URL}${url}`;
  return url;
};

export default function LandingPage() {
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [wordOfDay, setWordOfDay] = useState(null);
  const [stats, setStats] = useState({ total_words: 0, total_users: 0 });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [cmsContent, setCmsContent] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  
  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxImages, setLightboxImages] = useState([]);
  
  // Video player state
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [currentVideo, setCurrentVideo] = useState(null);
  
  // Community posts and popular links
  const [communityPosts, setCommunityPosts] = useState([]);
  const [popularLinks, setPopularLinks] = useState([]);

  // Handle scroll for header shrink effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    fetch(`${API}/words/daily`).then(res => res.json()).then(setWordOfDay).catch(console.error);
    fetch(`${API}/stats`).then(res => res.json()).then(setStats).catch(console.error);
    fetch(`${API}/cms/homepage`).then(res => res.json()).then(setCmsContent).catch(console.error);
    fetch(`${API}/cms/posts`).then(res => res.json()).then(setCommunityPosts).catch(console.error);
    fetch(`${API}/cms/popular-links`).then(res => res.json()).then(setPopularLinks).catch(console.error);
  }, []);

  // Open lightbox with images
  const openLightbox = (images, index = 0) => {
    setLightboxImages(images);
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  // Open video modal
  const openVideoModal = (video) => {
    setCurrentVideo(video);
    setVideoModalOpen(true);
  };

  // Extract YouTube video ID from URL
  const getYouTubeId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
    return match ? match[1] : null;
  };

  // Get content from CMS or use fallback
  const heroes = cmsContent?.heroes || [];
  const mainHero = heroes.find(h => h.is_main) || heroes[0];
  // Exclude the mainHero from sideHeroes to avoid duplication
  const sideHeroes = heroes.filter(h => h.id !== mainHero?.id).slice(0, 2);
  const articles = cmsContent?.articles || [];
  const videos = cmsContent?.videos || [];
  const gallery = cmsContent?.gallery || [];
  const hashtags = cmsContent?.hashtags || [];
  const settings = cmsContent?.settings || {};

  return (
    <div className="min-h-screen bg-background" data-testid="landing-page">
      {/* Header */}
      <header className={`header-main transition-all duration-300 ${isScrolled ? 'py-1 shadow-md' : ''}`}>
        <div className={`max-w-7xl mx-auto px-4 flex justify-between items-center transition-all duration-300 ${isScrolled ? 'py-1' : 'py-3'}`}>
          <Link to="/" className="flex items-center gap-3" data-testid="site-logo">
            <img 
              src={`${BACKEND_URL}/api/images/logo_mon_baoule.png`} 
              alt="Mon Baoulé" 
              className={`w-auto object-contain transition-all duration-300 ${isScrolled ? 'h-10 sm:h-12' : 'h-16 sm:h-20 lg:h-24'}`}
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            <Link to="/dictionary" className="nav-link text-foreground hover:text-primary">Dictionnaire</Link>
            <Link to="/learn" className="nav-link text-foreground hover:text-primary">Apprendre</Link>
            <Link to="/quiz" className="nav-link text-foreground hover:text-primary">Quiz</Link>
            <Link to="/trends" className="nav-link text-foreground hover:text-primary">Tendances</Link>
            <Link to="/community" className="nav-link text-foreground hover:text-primary">Communauté</Link>
          </nav>

          <div className="flex items-center gap-3">
            <button onClick={toggleTheme} className="p-2 rounded-lg hover:bg-muted transition-colors">
              {theme === 'dark' ? <Sun className="w-5 h-5 text-primary" /> : <Moon className="w-5 h-5" />}
            </button>
            
            {user ? (
              <Button onClick={() => navigate('/dashboard')} className={`rounded-full hidden sm:flex transition-all duration-300 ${isScrolled ? 'h-8 text-sm' : ''}`}>
                <Search className="w-4 h-4 mr-2" />
                Dashboard
              </Button>
            ) : (
              <Button onClick={() => navigate('/register')} className={`rounded-full hidden sm:flex transition-all duration-300 ${isScrolled ? 'h-8 text-sm' : ''}`}>
                <Search className="w-4 h-4 mr-2" />
                Commencer
              </Button>
            )}
            
            <button className="lg:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-background border-t border-border py-4 px-4 animate-slide-up">
            <div className="flex flex-col gap-3">
              <Link to="/dictionary" className="py-2 font-semibold">Dictionnaire</Link>
              <Link to="/learn" className="py-2 font-semibold">Apprendre</Link>
              <Link to="/quiz" className="py-2 font-semibold">Quiz</Link>
              <Link to="/community" className="py-2 font-semibold">Communauté</Link>
              <Button onClick={() => navigate(user ? '/dashboard' : '/register')} className="rounded-full mt-2">
                {user ? 'Dashboard' : 'Commencer'}
              </Button>
            </div>
          </div>
        )}
      </header>

      {/* Hero Section - Magazine Style */}
      <section className="max-w-7xl mx-auto px-4 py-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* Main Hero - Takes 3 columns */}
          {mainHero && (
            <div 
              className="lg:col-span-3 hero-card h-[300px] sm:h-[400px] lg:h-[480px] animate-slide-up"
              onClick={() => navigate(`/article/${mainHero.id}`)}
              data-testid={`hero-main-${mainHero.id}`}
            >
              <img src={getImageUrl(mainHero.image_url)} alt={mainHero.title} className="w-full h-full" />
              <div className="hero-overlay p-4 sm:p-6 lg:p-8">
                <span className="category-badge">{mainHero.category_icon} {mainHero.category}</span>
                <h1 className="font-heading text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-black mb-2 lg:mb-3 leading-tight max-w-3xl">
                  {mainHero.title}
                </h1>
                <p className="text-white/90 mb-3 hidden sm:block text-sm lg:text-base line-clamp-2 max-w-2xl">
                  {mainHero.excerpt}
                </p>
                <div className="flex flex-wrap gap-3 lg:gap-4 text-xs lg:text-sm text-white/80">
                  <span className="flex items-center gap-1">👤 {mainHero.author}</span>
                  <span className="flex items-center gap-1">📅 {mainHero.date}</span>
                  <span className="hidden sm:flex items-center gap-1">⏱️ {mainHero.read_time}</span>
                </div>
                <div className="mt-4 hidden lg:block">
                  <span className="inline-flex items-center gap-2 bg-white/20 hover:bg-white/30 px-4 py-2 rounded-full text-white text-sm font-semibold transition-colors">
                    Lire l'article <ChevronRight className="w-4 h-4" />
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Sidebar Heroes - Stacked vertically */}
          <div className="lg:col-span-1 grid grid-cols-2 lg:grid-cols-1 gap-4">
            {sideHeroes.map((hero, i) => (
              <div 
                key={hero.id || i} 
                className={`hero-card h-[180px] sm:h-[200px] lg:h-[232px] animate-slide-up stagger-${i + 1}`}
                onClick={() => navigate(`/article/${hero.id}`)}
                data-testid={`hero-side-${hero.id}`}
              >
                <img src={getImageUrl(hero.image_url)} alt={hero.title} className="w-full h-full" />
                <div className="hero-overlay p-3 sm:p-4">
                  <span className="category-badge text-xs py-0.5">{hero.category_icon} {hero.category}</span>
                  <h3 className="font-heading text-sm sm:text-base lg:text-lg font-bold leading-tight line-clamp-2">
                    {hero.title}
                  </h3>
                  <span className="hidden lg:inline-flex items-center gap-1 mt-2 text-xs text-white/70 hover:text-white transition-colors">
                    Lire <ChevronRight className="w-3 h-3" />
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trending Bar - Enhanced */}
      <div className="trending-bar cursor-pointer relative z-0" onClick={() => navigate('/trends')}>
        <div className="max-w-7xl mx-auto px-4 flex items-center gap-6 overflow-x-auto">
          <div className="trending-label flex items-center gap-2">
            <Flame className="w-4 h-4" />
            TENDANCES
          </div>
          <div className="flex gap-6">
            {hashtags.slice(0, 5).map((h, i) => (
              <span key={h.id || i} className="whitespace-nowrap font-semibold hover:text-primary transition-colors flex items-center gap-2">
                {h.tag}
                {h.trend === "up" && <TrendingUp className="w-4 h-4 text-green-500" />}
              </span>
            ))}
          </div>
          <Link to="/trends" className="ml-auto whitespace-nowrap text-primary font-bold text-sm hover:underline flex items-center gap-1">
            Voir tout <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </div>

      {/* Enhanced Trends Section */}
      <section className="max-w-7xl mx-auto px-4 py-12 bg-muted/30 dark:bg-muted/50 relative z-0 rounded-xl my-4">
        <div className="flex items-center justify-between mb-8">
          <h2 className="section-title flex items-center gap-3">
            <span className="text-4xl">🔥</span>
            Ce qui Buzz
          </h2>
          <Link to="/trends" className="text-primary font-bold uppercase text-sm hover:underline flex items-center">
            Explorer les Tendances <ChevronRight className="w-4 h-4 ml-1" />
          </Link>
        </div>
        
        <div className="grid md:grid-cols-4 gap-6">
          {/* Trending Word Card */}
          <div className="md:col-span-2 p-6 rounded-2xl bg-gradient-to-br from-primary to-destructive text-white">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-5 h-5" />
              <span className="font-bold uppercase text-sm opacity-90">Mot le plus recherché</span>
            </div>
            {wordOfDay && (
              <>
                <h3 className="font-heading text-4xl font-black mb-2">{wordOfDay.baoulé}</h3>
                <p className="text-xl opacity-90 mb-4">{wordOfDay.français}</p>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Eye className="w-4 h-4" />
                    <span className="text-sm">12.5K vues</span>
                  </div>
                  <div className="flex items-center gap-2 text-green-300">
                    <ArrowUp className="w-4 h-4" />
                    <span className="text-sm">+85%</span>
                  </div>
                </div>
              </>
            )}
          </div>
          
          {/* Challenge Card */}
          <div className="p-6 rounded-2xl bg-card border-2 border-accent hover:shadow-lg transition-all cursor-pointer" onClick={() => navigate('/trends')}>
            <div className="text-4xl mb-4">🎯</div>
            <h4 className="font-heading text-xl font-bold mb-2">Défi du Jour</h4>
            <p className="text-muted-foreground text-sm mb-4">Apprenez 5 nouveaux mots et gagnez 50 points</p>
            <div className="flex items-center justify-between">
              <Progress value={0} className="flex-1 h-2 mr-3" />
              <span className="text-primary font-bold text-sm">0/5</span>
            </div>
          </div>
          
          {/* Expression Card */}
          <div className="p-6 rounded-2xl bg-secondary text-white cursor-pointer hover:shadow-lg transition-all" onClick={() => navigate('/trends')}>
            <div className="flex items-center gap-2 mb-4">
              <Calendar className="w-5 h-5" />
              <span className="font-bold uppercase text-sm opacity-90">Expression</span>
            </div>
            <h4 className="font-heading text-lg font-bold mb-2">"Akwaba!"</h4>
            <p className="text-sm opacity-90">Bienvenue - L'hospitalité ivoirienne en un mot</p>
          </div>
        </div>
        
        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
          <div className="text-center p-4 rounded-xl bg-card border border-border">
            <p className="text-3xl font-black font-heading text-primary">156</p>
            <p className="text-sm text-muted-foreground">Mots appris aujourd'hui</p>
          </div>
          <div className="text-center p-4 rounded-xl bg-card border border-border">
            <p className="text-3xl font-black font-heading text-secondary">48</p>
            <p className="text-sm text-muted-foreground">Quiz complétés</p>
          </div>
          <div className="text-center p-4 rounded-xl bg-card border border-border">
            <p className="text-3xl font-black font-heading text-accent">312</p>
            <p className="text-sm text-muted-foreground">Audio joués</p>
          </div>
          <div className="text-center p-4 rounded-xl bg-card border border-border">
            <p className="text-3xl font-black font-heading text-destructive">67</p>
            <p className="text-sm text-muted-foreground">Apprenants actifs</p>
          </div>
        </div>
      </section>

      {/* Word of the Day Section */}
      <section className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid md:grid-cols-2 gap-8 items-center">
          <div className="animate-slide-up">
            <span className="category-badge mb-4">🎤 MOT DU JOUR</span>
            {wordOfDay ? (
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div>
                    <h2 className="font-heading text-5xl sm:text-6xl font-black text-primary">
                      {wordOfDay.baoulé}
                    </h2>
                    {wordOfDay.phonétique && (
                      <p className="font-mono text-muted-foreground text-lg">/{wordOfDay.phonétique}/</p>
                    )}
                  </div>
                  <AudioButton 
                    text={wordOfDay.baoulé}
                    phonetic={wordOfDay.phonétique}
                    size="lg"
                    className="shadow-lg"
                  />
                </div>
                <p className="text-3xl font-semibold">{wordOfDay.français}</p>
                <span className="inline-block px-4 py-2 rounded-full bg-secondary/10 text-secondary font-semibold">
                  {wordOfDay.catégorie}
                </span>
                {wordOfDay.exemple_baoulé && (
                  <blockquote className="border-l-4 border-primary pl-4 italic text-muted-foreground">
                    "{wordOfDay.exemple_baoulé}" — {wordOfDay.exemple_français}
                  </blockquote>
                )}
              </div>
            ) : (
              <div className="animate-pulse space-y-4">
                <div className="h-16 bg-muted rounded w-2/3"></div>
                <div className="h-8 bg-muted rounded w-1/2"></div>
              </div>
            )}
          </div>
          
          <div className="grid grid-cols-3 gap-4 animate-slide-up stagger-2">
            <div className="text-center p-6 rounded-2xl bg-primary/10">
              <BookOpen className="w-10 h-10 text-primary mx-auto mb-3" />
              <p className="text-3xl font-black font-heading">{stats.total_words}+</p>
              <p className="text-sm text-muted-foreground">Mots</p>
            </div>
            <div className="text-center p-6 rounded-2xl bg-secondary/10">
              <Users className="w-10 h-10 text-secondary mx-auto mb-3" />
              <p className="text-3xl font-black font-heading">{stats.total_users}+</p>
              <p className="text-sm text-muted-foreground">Apprenants</p>
            </div>
            <div className="text-center p-6 rounded-2xl bg-accent/20">
              <Headphones className="w-10 h-10 text-accent mx-auto mb-3" />
              <p className="text-3xl font-black font-heading">100%</p>
              <p className="text-sm text-muted-foreground">Audio</p>
            </div>
          </div>
        </div>
      </section>

      {/* Latest Articles */}
      <section className="max-w-7xl mx-auto px-4 py-12">
        <div className="section-header">
          <h2 className="section-title">Dernières Actualités</h2>
             <Link to="/articles" className="text-primary font-bold uppercase text-sm hover:underline flex items-center">
                Voir Tout <ChevronRight className="w-4 h-4 ml-1" />
              </Link>
        </div>
        
        <div className="grid md:grid-cols-3 gap-8">
          {articles.map((article, i) => (
            <article 
              key={article.id || i} 
              className="article-card animate-slide-up cursor-pointer group" 
              style={{animationDelay: `${i * 0.1}s`}}
              onClick={() => navigate(`/article/${article.id}`)}
              data-testid={`article-${article.id}`}
            >
              <div className="relative overflow-hidden">
                <img 
                  src={getImageUrl(article.image_url)} 
                  alt={article.title} 
                  className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300" 
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                  <span className="opacity-0 group-hover:opacity-100 transition-opacity bg-white text-foreground px-4 py-2 rounded-full font-semibold text-sm">
                    Lire l'article
                  </span>
                </div>
              </div>
              <div className="p-6">
                <div className="text-primary font-bold text-sm uppercase mb-2">{article.category}</div>
                <h3 className="font-heading text-xl font-bold mb-3 group-hover:text-primary transition-colors">{article.title}</h3>
                <p className="text-muted-foreground mb-4 line-clamp-2">{article.excerpt}</p>
                <div className="flex justify-between items-center text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-accent"></div>
                    <span>{article.author}</span>
                  </div>
                  <span>{article.time}</span>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Section Prénoms Baoulé */}
      <PrenomsSection />

      {/* Video Section */}
      <section className="bg-foreground text-background py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between mb-8">
            <h2 className="section-title text-background">📹 Vidéos de la Semaine</h2>
            <Link to="/videos" className="text-primary font-bold uppercase text-sm hover:underline flex items-center">
              Voir Toutes <ChevronRight className="w-4 h-4 ml-1" />
            </Link>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {videos.map((video, i) => (
              <div 
                key={video.id || i} 
                className="video-card animate-slide-up cursor-pointer group" 
                style={{animationDelay: `${i * 0.1}s`}}
                onClick={() => openVideoModal(video)}
                data-testid={`video-${video.id || i}`}
              >
                <div className="relative overflow-hidden">
                  <img 
                    src={getImageUrl(video.image_url)} 
                    alt={video.title} 
                    className="w-full h-56 object-cover group-hover:scale-105 transition-transform duration-500" 
                  />
                  <div className="absolute inset-0 bg-black/30 group-hover:bg-black/50 transition-colors" />
                  <div className="play-button group-hover:scale-110 group-hover:bg-primary transition-all">
                    <Play className="w-8 h-8 fill-current" />
                  </div>
                  <div className="absolute bottom-3 right-3 bg-black/80 px-2 py-1 rounded text-xs font-semibold">
                    {video.duration}
                  </div>
                </div>
                <div className="p-5 bg-white/5">
                  <h3 className="font-heading text-lg font-bold mb-2 group-hover:text-primary transition-colors">{video.title}</h3>
                  <div className="flex gap-4 text-sm opacity-70">
                    <span className="flex items-center gap-1"><Eye className="w-4 h-4" /> {video.views}</span>
                    <span className="flex items-center gap-1"><Heart className="w-4 h-4" /> {video.likes}</span>
                    <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {video.duration}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <div className="section-header">
          <h2 className="section-title">📸 Galerie Culturelle</h2>
          <Link to="/Gallery" className="text-primary font-bold uppercase text-sm hover:underline flex items-center">
            Voir Toutes <ChevronRight className="w-4 h-4 ml-1" />
          </Link>
        </div>
        
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {gallery.map((item, i) => (
            <div 
              key={item.id || i} 
              className="gallery-item animate-slide-up cursor-pointer group" 
              style={{animationDelay: `${i * 0.1}s`}}
              onClick={() => openLightbox(gallery, i)}
              data-testid={`gallery-item-${i}`}
            >
              <img src={getImageUrl(item.image_url)} alt={item.title} className="group-hover:scale-110 transition-transform duration-500" />
              <div className="gallery-caption">
                <h4 className="font-heading font-bold text-lg">{item.title}</h4>
                <p className="text-sm opacity-80">{item.subtitle}</p>
              </div>
              <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="p-2 bg-white/20 backdrop-blur rounded-full">
                  <Maximize2 className="w-5 h-5 text-white" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Community & Sidebar */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Feed */}
          <div className="lg:col-span-2 space-y-6">
            <div className="section-header">
              <h2 className="section-title">💬 Fil Communautaire</h2>
            </div>
            
            {communityPosts.length > 0 ? communityPosts.slice(0, 3).map((post, index) => (
              <div key={post.id || index} className="post-card animate-slide-up mb-4">
                <div className="flex items-center gap-4 mb-4">
                  <div className="post-avatar">{post.author_name?.charAt(0) || "?"}</div>
                  <div>
                    <div className="font-bold">{post.author_name}</div>
                    <div className="text-sm text-muted-foreground">{post.time}</div>
                  </div>
                </div>
                <p className="mb-4">{post.content}</p>
                {post.image_url && (
                  <img 
                    src={getImageUrl(post.image_url)} 
                    alt="Post" 
                    className="w-full rounded-lg mb-4 cursor-pointer hover:opacity-90 transition-opacity" 
                    onClick={() => openLightbox([{ image_url: post.image_url, title: post.author_name }], 0)}
                  />
                )}
                <div className="flex gap-6 pt-4 border-t border-border">
                  <button className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors">
                    <Heart className="w-5 h-5" /> {post.likes || 0} J'aime
                  </button>
                  <button className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors">
                    <MessageCircle className="w-5 h-5" /> {post.comments || 0} Commentaires
                  </button>
                  <button className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors">
                    <Share2 className="w-5 h-5" /> Partager
                  </button>
                </div>
              </div>
            )) : (
              <div className="post-card animate-slide-up">
                <div className="flex items-center gap-4 mb-4">
                  <div className="post-avatar">K</div>
                  <div>
                    <div className="font-bold">Kouadio Emmanuel</div>
                    <div className="text-sm text-muted-foreground">Il y a 30 minutes</div>
                  </div>
                </div>
                <p className="mb-4">
                  🎉 J'ai terminé ma première semaine d'apprentissage ! Le mot "Akwaba" est parfaitement ancré. 
                  Merci à la communauté pour vos encouragements !
                </p>
                <img 
                  src={getImageUrl("/api/images/hero_culture_baoule.png")} 
                  alt="Célébration" 
                  className="w-full rounded-lg mb-4 cursor-pointer hover:opacity-90 transition-opacity" 
                  onClick={() => openLightbox([{ image_url: "/api/images/hero_culture_baoule.png", title: "Célébration culturelle" }], 0)}
                />
                <div className="flex gap-6 pt-4 border-t border-border">
                  <button className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors">
                    <Heart className="w-5 h-5" /> 127 J'aime
                  </button>
                  <button className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors">
                    <MessageCircle className="w-5 h-5" /> 23 Commentaires
                  </button>
                  <button className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors">
                    <Share2 className="w-5 h-5" /> Partager
                  </button>
                </div>
              </div>
            )}
          </div>
          
          {/* Sidebar */}
          <div className="space-y-6">
            <div className="widget">
              <h3 className="font-heading text-xl font-bold mb-6">🔥 Populaire</h3>
              {popularLinks.length > 0 ? popularLinks.map((link, index) => (
                <div 
                  key={link.id || index} 
                  className="flex gap-4 mb-5 cursor-pointer hover:bg-muted/50 p-2 rounded-lg transition-colors group"
                  onClick={() => navigate(link.url)}
                  data-testid={`popular-${index + 1}`}
                >
                  <div className="popular-number group-hover:bg-primary group-hover:text-white transition-colors">{index + 1}</div>
                  <div>
                    <div className="font-bold leading-tight group-hover:text-primary transition-colors">{link.title}</div>
                  </div>
                </div>
              )) : (
                [
                  { title: "🎓 Guide Débutant", link: "/learn" },
                  { title: "📖 Dictionnaire", link: "/dictionary" },
                  { title: "🎮 Quiz Express", link: "/quiz" },
                ].map((item, index) => (
                  <div 
                    key={index} 
                    className="flex gap-4 mb-5 cursor-pointer hover:bg-muted/50 p-2 rounded-lg transition-colors group"
                    onClick={() => navigate(item.link)}
                    data-testid={`popular-${index + 1}`}
                  >
                    <div className="popular-number group-hover:bg-primary group-hover:text-white transition-colors">{index + 1}</div>
                    <div>
                      <div className="font-bold leading-tight group-hover:text-primary transition-colors">{item.title}</div>
                    </div>
                  </div>
                ))
              )}
            </div>
            
            <div className="newsletter-widget">
              <h3 className="font-heading text-2xl font-bold mb-3">📬 Newsletter</h3>
              <p className="opacity-90 mb-4">Mot de la semaine, vidéos exclusives et actualités culturelles.</p>
              <div className="flex gap-2">
                <input 
                  type="email" 
                  placeholder="Votre email" 
                  className="flex-1 px-4 py-3 rounded-lg text-foreground"
                />
                <Button className="bg-foreground text-background hover:bg-secondary rounded-lg px-6">
                  OK
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-primary py-20 px-4">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="font-heading text-4xl sm:text-5xl font-black mb-6">
            Prêt à parler baoulé ?
          </h2>
          <p className="text-xl opacity-90 mb-8">
            Rejoignez des centaines d'apprenants passionnés par cette belle langue africaine.
          </p>
          <Button 
            size="lg" 
            className="bg-white text-primary hover:bg-white/90 rounded-full px-10 text-lg font-bold shadow-xl hover:shadow-2xl hover:-translate-y-1 transition-all"
            onClick={() => navigate(user ? '/dashboard' : '/register')}
            data-testid="cta-button"
          >
            Commencer maintenant
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-foreground text-background py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            <div>
              <div className="flex items-center gap-2 mb-4 bg-white rounded-lg p-2 w-fit">
                <img 
                  src={`${BACKEND_URL}/api/images/logo_mon_baoule.png`} 
                  alt="Mon Baoulé" 
                  className="h-10 w-auto object-contain"
                />
              </div>
              <p className="opacity-70 mb-4">
                La plateforme de référence pour découvrir et apprendre la langue baoulé.
              </p>
              <div className="flex gap-4 text-xl">
                <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">📘</a>
                <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">📷</a>
                <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">🐦</a>
                <a href="https://youtube.com" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">▶️</a>
              </div>
            </div>
            
            <div>
              <h3 className="font-heading text-lg font-bold mb-4">Explorer</h3>
              <ul className="space-y-2 opacity-70">
                <li><Link to="/dictionary" className="hover:text-primary transition-colors">Dictionnaire</Link></li>
                <li><Link to="/quiz" className="hover:text-primary transition-colors">Quiz</Link></li>
                <li><Link to="/learn" className="hover:text-primary transition-colors">Flashcards</Link></li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-heading text-lg font-bold mb-4">Communauté</h3>
              <ul className="space-y-2 opacity-70">
                <li><Link to="/community" className="hover:text-primary transition-colors">Forum</Link></li>
                <li><Link to="/contribute" className="hover:text-primary transition-colors">Contribuer</Link></li>
                <li><Link to="/trends" className="hover:text-primary transition-colors">Événements</Link></li>
                <li><Link to="/trends" className="hover:text-primary transition-colors">Classement</Link></li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-heading text-lg font-bold mb-4">Ressources</h3>
              <ul className="space-y-2 opacity-70">
                <li><Link to="/learn" className="hover:text-primary transition-colors">Guide Débutant</Link></li>
                <li><Link to="/dictionary" className="hover:text-primary transition-colors">FAQ</Link></li>
                <li><Link to="/trends" className="hover:text-primary transition-colors">Blog</Link></li>
                <li><Link to="/community" className="hover:text-primary transition-colors">Contact</Link></li>
              </ul>
            </div>
          </div>
          
          <div className="pt-8 border-t border-white/10 text-center opacity-50">
            <p>© 2026 Mon Baoulé. Tous droits réservés.</p>
          </div>
        </div>
      </footer>

      {/* Lightbox Modal */}
      {lightboxOpen && (
        <Lightbox
          images={lightboxImages}
          currentIndex={lightboxIndex}
          onClose={() => setLightboxOpen(false)}
          onNavigate={(index) => setLightboxIndex(index)}
        />
      )}

      {/* Video Modal */}
      {videoModalOpen && currentVideo && (
        <div 
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4"
          onClick={() => setVideoModalOpen(false)}
        >
          <button
            onClick={() => setVideoModalOpen(false)}
            className="absolute top-4 right-4 z-10 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
          
          <div 
            className="w-full max-w-5xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Video Title */}
            <h2 className="text-white font-heading text-xl sm:text-2xl font-bold mb-4 text-center">
              {currentVideo.title}
            </h2>
            
            {/* Video Player */}
            <div className="relative w-full aspect-video bg-black rounded-xl overflow-hidden shadow-2xl">
              {currentVideo.video_url && getYouTubeId(currentVideo.video_url) ? (
                <iframe
                  src={`https://www.youtube.com/embed/${getYouTubeId(currentVideo.video_url)}?autoplay=1&rel=0`}
                  title={currentVideo.title}
                  className="absolute inset-0 w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-white">
                  <img 
                    src={getImageUrl(currentVideo.image_url)} 
                    alt={currentVideo.title}
                    className="absolute inset-0 w-full h-full object-cover opacity-30"
                  />
                  <div className="relative z-10 text-center p-8">
                    <Play className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p className="text-lg mb-2">Vidéo non disponible</p>
                    <p className="text-sm text-white/60">
                      Cette vidéo n'a pas encore de lien YouTube configuré.
                    </p>
                    {currentVideo.video_url && (
                      <a 
                        href={currentVideo.video_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block mt-4 px-6 py-2 bg-primary text-white rounded-full hover:bg-primary/80 transition-colors"
                      >
                        Ouvrir le lien externe
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            {/* Video Info */}
            <div className="flex justify-center gap-6 mt-4 text-white/70 text-sm">
              <span className="flex items-center gap-1"><Eye className="w-4 h-4" /> {currentVideo.views} vues</span>
              <span className="flex items-center gap-1"><Heart className="w-4 h-4" /> {currentVideo.likes}</span>
              <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {currentVideo.duration}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
